import {useMemo, useState, useEffect} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import {PageWrapper, PageHeader, PageContentWrapper} from '../../../Layout';
import {getProgramData} from '../../../api/programs';
import {getSiteTypes} from '../../../api/siteType';
import {getWeatherConditions} from '../../../api/weatherCondition';
import {getResources} from '../../../api/resource';
import {getWasteTypes} from '../../../api/wasteType';
import {getWasteCategories} from '../../../api/wasteCategory';
import {getAdditionalInfo} from '../../../api/additionalInfo';
import {getSubstrateOptions} from '../../../api/substrateOptions';
import {getUsers} from '../../../api/user';
import {
    fetchCleanUById,
    updateCleanUp,
    updateLocation,
    updateCollectedWeight,
    updateCollectedItems,
    updateLitterBags,
    updateResources,
    updateSiteTypes,
    updateAdditionalInfo,
    updateWeatherConditions,
} from '../../../api/cleanUp';
import {replaceCleanUp} from '../../../store/cleanUp/cleanUpSlice';
import {useAppDispatch} from '../../../store/app-hooks';
import {ICleanUp} from '../../../models/cleanUp';
import {IProgram} from '../../../models/program';
import {IUser} from '../../../models/user';
import {ActivityStatus} from '../../../models/activity';
import {ISiteType} from '../../../models/siteType';
import {IWeatherCondition} from '../../../models/weatherCondition';
import {IResource, ResourceType} from '../../../models/resource';
import {IWasteType} from '../../../models/wasteType';
import {IWasteCategory} from '../../../models/wasteCategory';
import {IAdditionalInfo} from '../../../models/additionalInfo';
import {ISubstrateOption} from '../../../models/substrateOption';
import {ITeam} from '../../../models/team';
import {
    ICreateCleanUpForm,
    ILitterBagEntry,
    IGeneralResourceEntry,
    IWeatherEntry,
} from '../CleanUpCreate/index';
import Step2Litter from '../CleanUpCreate/steps/Step2BasicInfo';
import Step3Location from '../CleanUpCreate/steps/Step3Location';
import Step4Resources from '../CleanUpCreate/steps/Step4Collection';
import Step5Remarks from '../CleanUpCreate/steps/Step5Environmental';
import StepGallery from '../CleanUpCreate/steps/StepGallery';
import Step6Review from '../CleanUpCreate/steps/Step6Notes';
import {convertDateToString} from '../../../utils/date_helpers';
import s from '../CleanUpCreate/index.module.scss';

// ─── Config data ──────────────────────────────────────────────────────────────

interface IAllConfig {
    siteTypes: ISiteType[];
    weatherConditions: IWeatherCondition[];
    resources: IResource[];
    wasteTypes: IWasteType[];
    wasteCategories: IWasteCategory[];
    additionalInfo: IAdditionalInfo[];
    substrateOptions: ISubstrateOption[];
    users: IUser[];
}

// ─── Step icons (same as Create) ─────────────────────────────────────────────

const LitterIcon = (
    <svg width="18" height="18" viewBox="0 0 38 38" fill="currentColor">
        <path d="M25.4074 33.0945H12.7405C11.5871 33.1575 10.4512 32.7954 9.5541 32.0789C8.65698 31.3625 8.0629 30.3428 7.8876 29.2187L6.40288 14.5339C6.39074 14.418 6.40334 14.301 6.43986 14.1902C6.47639 14.0795 6.53604 13.9775 6.61498 13.8908C6.6943 13.8042 6.79114 13.7349 6.89927 13.6874C7.00739 13.64 7.12442 13.6153 7.24281 13.615H19.1206C19.3456 13.615 19.5614 13.7031 19.7205 13.8598C19.8796 14.0164 19.969 14.2288 19.969 14.4504C19.969 14.6719 19.8796 14.8844 19.7205 15.0411C19.5614 15.1977 19.3456 15.2857 19.1206 15.2857H8.18455L9.57595 29.0516C9.7222 29.7572 10.1236 30.3867 10.7056 30.823C11.2876 31.2593 12.0107 31.4728 12.7405 31.4239H25.4074C26.1398 31.4746 26.8659 31.2609 27.4499 30.8227C28.0339 30.3846 28.4358 29.7519 28.5804 29.0432L30.0058 15.0101C29.8959 14.8902 29.8237 14.7417 29.7978 14.5823C29.7719 14.4229 29.7935 14.2595 29.8599 14.1119C29.9263 13.9644 30.0347 13.839 30.172 13.7508C30.3093 13.6626 30.4696 13.6154 30.6336 13.615H30.9136C31.0319 13.6153 31.149 13.64 31.2571 13.6874C31.3652 13.7349 31.4621 13.8042 31.5414 13.8908C31.6218 13.9768 31.683 14.0784 31.721 14.1892C31.759 14.3 31.773 14.4174 31.762 14.5339L30.2688 29.2103C30.0934 30.3367 29.4985 31.3584 28.5997 32.0765C27.701 32.7946 26.563 33.1575 25.4074 33.0945Z" />
        <path d="M30.6336 15.3108H19.0782C18.8532 15.3108 18.6374 15.2228 18.4783 15.0661C18.3192 14.9095 18.2298 14.697 18.2298 14.4754C18.2298 14.2539 18.3192 14.0414 18.4783 13.8847C18.6374 13.7281 18.8532 13.6401 19.0782 13.6401H30.6336C30.8586 13.6401 31.0744 13.7281 31.2335 13.8847C31.3926 14.0414 31.482 14.2539 31.482 14.4754C31.482 14.697 31.3926 14.9095 31.2335 15.0661C31.0744 15.2228 30.8586 15.3108 30.6336 15.3108Z" />
        <path fillRule="evenodd" clipRule="evenodd" d="M21.9373 29.035C20.957 29.035 19.9987 28.7486 19.1838 28.2122C18.3688 27.6758 17.7337 26.9134 17.359 26.0216C16.9842 25.1297 16.8866 24.1485 17.0784 23.202C17.2703 22.2555 17.743 21.3862 18.4368 20.7043C19.1306 20.0224 20.0142 19.5585 20.9759 19.3712C21.9376 19.1839 22.9341 19.2817 23.8393 19.6522C24.7445 20.0227 25.5178 20.6493 26.0612 21.4526C26.6046 22.256 26.8938 23.1999 26.8921 24.165C26.8898 25.4574 26.3668 26.696 25.4379 27.6091C24.5089 28.5221 23.2499 29.035 21.9373 29.035Z" />
        <path d="M7.15797 12.12C6.93295 12.1388 6.70956 12.0688 6.53692 11.9255C6.36429 11.7822 6.25656 11.5772 6.23744 11.3556C6.21831 11.1341 6.28935 10.9141 6.43494 10.7441C6.58052 10.5742 6.78872 10.4682 7.01374 10.4493L30.4894 6.37302C30.6008 6.35383 30.7149 6.35643 30.8253 6.38067C30.9357 6.40492 31.0402 6.45026 31.1328 6.51425C31.2253 6.57824 31.3042 6.65957 31.3648 6.75356C31.4255 6.84756 31.4667 6.95242 31.4862 7.06211C31.5057 7.17181 31.5031 7.2842 31.4785 7.39289C31.4539 7.50158 31.4077 7.60449 31.3427 7.69563C31.2777 7.78677 31.1951 7.86432 31.0997 7.92404C31.0042 7.98375 30.8977 8.02445 30.7863 8.04364L7.3022 12.12H7.15797Z" />
        <path d="M14.0216 8.10198C13.7966 8.12081 13.5732 8.05087 13.4006 7.90753C13.228 7.7642 13.1202 7.55918 13.1011 7.33764C13.082 7.1161 13.153 6.89622 13.2986 6.72625C13.4442 6.55628 13.6524 6.45019 13.8774 6.43136L22.6415 4.91114C22.8665 4.87237 23.098 4.92313 23.2849 5.05237C23.4719 5.1816 23.599 5.3787 23.6384 5.60023C23.6778 5.82177 23.6262 6.04969 23.4949 6.23375C23.3636 6.41782 23.1635 6.54299 22.9385 6.58176L14.1744 8.10198H14.0216Z" />
    </svg>
);

const LocationIcon = (
    <svg width="18" height="18" viewBox="0 0 38 38" fill="currentColor">
        <path d="M29.1175 14.9042C29.1175 13.3071 27.8351 12.0191 26.245 12.0191C24.655 12.0191 23.3726 13.3071 23.3726 14.9042C23.3726 16.5013 24.655 17.7893 26.245 17.7893C27.8351 17.7893 29.1175 16.5013 29.1175 14.9042Z" />
        <path fillRule="evenodd" clipRule="evenodd" d="M26.245 31.6172L26.7891 30.7941C27.2214 30.077 27.7935 29.1855 28.4394 28.1789C28.6085 27.9154 28.7828 27.6438 28.9608 27.3656C29.8152 26.0305 30.7521 24.5486 31.6211 23.0657C32.4891 21.5847 33.2969 20.0898 33.8894 18.7287C34.4736 17.3867 34.8828 16.0997 34.8828 15.0587C34.8828 10.2752 31.0075 6.38281 26.245 6.38281C24.0223 6.38281 21.9928 7.23068 20.4603 8.6213C18.851 7.78564 17.0445 7.33077 15.1144 7.33077C8.54884 7.33077 3.26562 12.6888 3.26562 19.2318C3.26562 25.8264 8.60013 31.1329 15.1144 31.1329L15.0631 31.0299C18.4241 31.0299 21.4357 29.6695 23.5735 27.4348C23.7346 27.6864 23.8923 27.9323 24.046 28.1717L24.0504 28.1786C24.6964 29.1853 25.2686 30.077 25.701 30.7941L26.245 31.6172ZM26.245 7.6605C22.1851 7.6605 18.8793 10.9809 18.8793 15.0587C18.8793 15.821 19.1946 16.9039 19.7661 18.2168C20.3293 19.5105 21.1083 20.9555 21.9652 22.4176C22.8209 23.8777 23.7465 25.3421 24.5993 26.6748C24.774 26.9478 24.9459 27.2157 25.1136 27.4771C25.5223 28.1138 25.9061 28.712 26.245 29.2512C26.5839 28.712 26.9678 28.1138 27.3764 27.477L27.3771 27.4759C27.5446 27.2149 27.7163 26.9474 27.8907 26.6748C28.7435 25.3421 29.6692 23.8777 30.5249 22.4176C31.3817 20.9555 32.1607 19.5105 32.7239 18.2168C33.2954 16.9039 33.6107 15.821 33.6107 15.0587C33.6107 10.9809 30.305 7.6605 26.245 7.6605ZM15.9351 27.7326L16.6532 21.7563H13.6782L13.6269 22.3745C13.9859 22.3745 14.1911 22.5806 14.1398 22.9928L13.5756 27.7841C13.5243 28.1963 13.2678 28.4023 12.9088 28.4023L12.8575 29.0206H16.3967L16.448 28.4023C16.089 28.4023 15.9351 28.1963 15.9351 27.7841V27.7326ZM15.3196 20.5713C15.7299 20.5713 16.089 20.4168 16.3967 20.1592C16.7045 19.8501 16.9097 19.5409 16.9609 19.1288C17.0122 18.7166 16.9097 18.356 16.6532 18.0984C16.3967 17.7893 16.0377 17.6862 15.6273 17.6862C15.217 17.6862 14.8579 17.8408 14.5502 18.0984C14.2424 18.4075 14.0372 18.7166 13.9859 19.1288C13.9346 19.5409 14.0372 19.9016 14.2937 20.1592C14.5502 20.4683 14.9092 20.5713 15.3196 20.5713ZM9.06177 15.2648H15.3196C15.7812 15.2648 16.1403 14.9042 16.1403 14.4405C16.1403 13.9768 15.7812 13.6162 15.3196 13.6162H9.06177C8.60013 13.6162 8.24108 13.9768 8.24108 14.4405C8.24108 14.9042 8.60013 15.2648 9.06177 15.2648ZM9.06177 12.4312H16.6019C17.0635 12.4312 17.4226 12.0706 17.4226 11.6069C17.4226 11.1432 17.0635 10.7826 16.6019 10.7826H9.06177C8.60013 10.7826 8.24108 11.1432 8.24108 11.6069C8.24108 12.0706 8.60013 12.4312 9.06177 12.4312Z" />
    </svg>
);

const ResourcesIcon = (
    <svg width="18" height="18" viewBox="0 0 38 38" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd" d="M31 8.5L19.5 6L7.5 8.5L2 18L7 20V27.5L19 32L31.5 27V18.48L29.5 19.36V26L20 29.5V16L23.5 22L29.5 19.36L31.5 18.48L36 16.5L31 8.5ZM19 8L12 9.5L19 11.5L26.5 9.5L19 8ZM17.5 13L8.5 10.5L4.5 17L13 20L17.5 13ZM9 26V20.5L14 22.5L18 16V29.5L9 26ZM30 10.5L20.5 13L24.5 19.36L33 15.5L30 10.5Z" />
    </svg>
);

const RemarksIcon = (
    <svg width="18" height="18" viewBox="0 0 38 38" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd" d="M3.11719 19C3.11719 25.9982 8.78726 31.6172 15.7344 31.6172L15.6833 31.5661C21.5408 31.5661 26.5104 27.5704 27.9017 22.1451L31.1531 18.8937C31.1548 18.892 31.1565 18.8903 31.1582 18.8885L31.2061 18.8407C31.2132 18.8336 31.2201 18.8263 31.2269 18.8189L34.734 15.2617C34.9815 15.0107 34.9801 14.6069 34.7308 14.3576L29.3161 8.94296C29.0655 8.69241 28.6593 8.69241 28.4087 8.94296L25.8778 11.4739C23.5497 8.39782 19.8939 6.38281 15.7344 6.38281C8.73618 6.38281 3.11719 12.0529 3.11719 19ZM30.7489 17.4761L33.3729 14.8145L28.8624 10.304L26.2196 12.9468L30.7489 17.4761ZM25.2867 13.9308L29.794 18.4381L23.179 25.0531L17.4386 26.2862L18.6717 20.5458L25.2867 13.9308ZM8.27644 13.7897C8.27644 13.3299 8.6851 12.9213 9.14483 12.9213C9.60457 12.9213 10.0132 13.3299 10.0132 13.7897C10.0132 14.2494 9.60457 14.6581 9.14483 14.6581C8.6851 14.6581 8.27644 14.2494 8.27644 13.7897ZM8.27644 17.2632C8.27644 16.8035 8.6851 16.3948 9.14483 16.3948C9.60457 16.3948 10.0132 16.8035 10.0132 17.2632C10.0132 17.723 9.60457 18.1316 9.14483 18.1316C8.6851 18.1316 8.27644 17.723 8.27644 17.2632ZM8.27644 20.6346C8.27644 20.1238 8.6851 19.7662 9.14483 19.7662C9.60457 19.7662 10.0132 20.1749 10.0132 20.6346C10.0132 21.0944 9.60457 21.503 9.14483 21.503C8.6851 21.503 8.27644 21.0944 8.27644 20.6346ZM15.4279 21.4519H11.9032C11.4435 21.4519 11.0349 21.0433 11.0349 20.5835C11.0349 20.0727 11.4435 19.7151 11.9032 19.7151H15.4279C15.8876 19.7151 16.2963 20.1238 16.2963 20.5835C16.2963 21.0433 15.8876 21.4519 15.4279 21.4519ZM17.6755 18.0805H11.9032C11.4435 18.0805 11.0349 17.6719 11.0349 17.2121C11.0349 16.7524 11.4435 16.3438 11.9032 16.3438H17.6755C18.1352 16.3438 18.5439 16.7524 18.5439 17.2121C18.5439 17.6719 18.1352 18.0805 17.6755 18.0805ZM19.9231 14.7091H11.9032C11.4435 14.7091 11.0349 14.3005 11.0349 13.8407C11.0349 13.3299 11.4435 12.9724 11.9032 12.9724H19.9231C20.3828 12.9724 20.7915 13.381 20.7915 13.8407C20.7915 14.3005 20.3828 14.7091 19.9231 14.7091Z" />
    </svg>
);

const GalleryIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
    </svg>
);

// ─── Stepper config ───────────────────────────────────────────────────────────

const STEPS: {label: string; description: string; icon?: JSX.Element}[] = [
    {label: 'Overview', description: 'Program & date'},
    {label: 'Litter', description: 'Waste collected', icon: LitterIcon},
    {label: 'Location', description: 'Site & coordinates', icon: LocationIcon},
    {label: 'Resources', description: 'Bags & tools', icon: ResourcesIcon},
    {label: 'Remarks', description: 'Activity & notes', icon: RemarksIcon},
    {label: 'Gallery', description: 'Before & after photos', icon: GalleryIcon},
    {label: 'Review', description: 'Summary & save'},
];

// ─── Pre-populate form from ICleanUp ─────────────────────────────────────────

const formFromCleanUp = (cu: ICleanUp): ICreateCleanUpForm => ({
    programId: cu.program,
    date: cu.date.slice(0, 10),
    collectorId: cu.created_by?.id ?? '',
    no_litter_found: cu.no_litter_found ?? false,
    collected_weights: Object.fromEntries(
        cu.collected_weights.map((w) => [
            w.waste_type_id,
            {wet: String(w.wet ?? ''), dry: String(w.dry ?? '')},
        ])
    ),
    collected_items: Object.fromEntries(
        Object.entries(cu.collected_items).map(([catId, items]) => [
            catId,
            Object.fromEntries(items.map((item) => [item.waste_category_item, String(item.amount)])),
        ])
    ),
    site_types: cu.site_types ?? [],
    latitude: String(cu.location?.latitude ?? ''),
    longitude: String(cu.location?.longitude ?? ''),
    location_name: cu.location_name ?? '',
    district: cu.district ?? '',
    municipality: cu.municipality ?? '',
    city_island: cu.city_island ?? '',
    area: cu.area != null ? String(cu.area) : '',
    length: cu.length != null ? String(cu.length) : '',
    substrate_id: cu.substrate ?? '',
    additional_location_info: cu.additional_location_info ?? [],
    litter_bags: (cu.used_liter_bags ?? []).map(
        (lb): ILitterBagEntry => ({
            resource_id: lb.resource,
            full: String(lb.full ?? ''),
            half: String(lb.half ?? ''),
        })
    ),
    general_resources: (cu.used_general_resources ?? []).map(
        (gr): IGeneralResourceEntry => ({
            resource_id: gr.resource,
            amount: String(gr.amount ?? ''),
        })
    ),
    activityId: cu.activity ?? '',
    team_id: cu.team ?? '',
    num_of_participant: String(cu.num_of_participant ?? ''),
    duration_from_hour: String(cu.duration?.from.hour ?? ''),
    duration_from_minute: String(cu.duration?.from.minute ?? ''),
    duration_to_hour: String(cu.duration?.to.hour ?? ''),
    duration_to_minute: String(cu.duration?.to.minute ?? ''),
    weather_conditions: (cu.weather_conditions ?? []).map(
        (wc): IWeatherEntry => ({
            condition_id: wc.condition_id,
            option_id: wc.option_id ?? null,
            value: wc.value != null ? String(wc.value) : '',
        })
    ),
    notes: cu.notes ?? '',
});

function toDMS(decimal: number, isLat: boolean): string {
    const abs = Math.abs(decimal);
    const deg = Math.floor(abs);
    const minFloat = (abs - deg) * 60;
    const min = Math.floor(minFloat);
    const sec = ((minFloat - min) * 60).toFixed(2);
    const dir = isLat ? (decimal >= 0 ? 'N' : 'S') : decimal >= 0 ? 'E' : 'W';
    return `${deg}°${min}'${sec}"${dir}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CleanUpEdit = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {id} = useParams<{id: string}>();
    const location = useLocation();

    const locationState = location.state as {initialStep?: number; returnDate?: string} | null;
    const [currentStep, setCurrentStep] = useState(locationState?.initialStep ?? 0);
    const returnDate = locationState?.returnDate;
    const [form, setForm] = useState<ICreateCleanUpForm | null>(null);
    const [originalCleanUp, setOriginalCleanUp] = useState<ICleanUp | null>(null);

    const [programs, setPrograms] = useState<IProgram[]>([]);
    const [allConfig, setAllConfig] = useState<IAllConfig | null>(null);
    const [loading, setLoading] = useState(true);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [showStepErrors, setShowStepErrors] = useState(false);

    useEffect(() => {
        if (!id) return;
        Promise.all([
            fetchCleanUById(id),
            getProgramData(),
            getSiteTypes(),
            getWeatherConditions(),
            getResources(),
            getWasteTypes(),
            getWasteCategories(),
            getAdditionalInfo(),
            getSubstrateOptions(),
            getUsers(),
        ])
            .then(
                ([
                    cu,
                    progs,
                    siteTypes,
                    weatherConditions,
                    resources,
                    wasteTypes,
                    wasteCategories,
                    additionalInfo,
                    substrateOptions,
                    users,
                ]) => {
                    setOriginalCleanUp(cu);
                    setForm(formFromCleanUp(cu));
                    setPrograms(progs);
                    setAllConfig({
                        siteTypes,
                        weatherConditions,
                        resources,
                        wasteTypes,
                        wasteCategories,
                        additionalInfo,
                        substrateOptions,
                        users,
                    });
                }
            )
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id]);

    const programScopedData = useMemo(() => {
        if (!originalCleanUp || !allConfig) return null;
        const programId = originalCleanUp.program;
        const program = programs.find((p) => p.id === programId);
        const ongoingActivities =
            program?.activities.filter((a) => a.status === ActivityStatus.ONGOING) ?? [];
        return {
            activities: ongoingActivities,
            teams: (program?.teams ?? []) as ITeam[],
            siteTypes: allConfig.siteTypes,
            weatherConditions: allConfig.weatherConditions.filter((wc) =>
                wc.programs.includes(programId)
            ),
            litterBags: allConfig.resources.filter(
                (r) => r.programs.includes(programId) && r.type === ResourceType.LITTER_BAG
            ),
            generalResources: allConfig.resources.filter(
                (r) => r.programs.includes(programId) && r.type === ResourceType.GENERAL
            ),
            wasteTypes: allConfig.wasteTypes.filter((wt) => wt.programs.includes(programId)),
            wasteCategories: allConfig.wasteCategories,
            additionalInfo: allConfig.additionalInfo.filter((ai) =>
                ai.programs.includes(programId)
            ),
            substrateOptions: allConfig.substrateOptions.filter((so) =>
                so.programs.includes(programId)
            ),
        };
    }, [originalCleanUp, programs, allConfig]);

    // If the saved activity is no longer ongoing, clear it from the form
    useEffect(() => {
        if (!programScopedData || !form) return;
        if (form.activityId && !programScopedData.activities.some((a) => a.id === form.activityId)) {
            setForm((prev) => prev && {...prev, activityId: ''});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programScopedData]);

    const updateForm = (updates: Partial<ICreateCleanUpForm>) =>
        setForm((prev) => prev && {...prev, ...updates});

    const canGoNext = () => {
        if (currentStep === 2) return !!form?.latitude && !!form?.longitude;
        return true;
    };

    const handleNext = () => {
        if (!canGoNext()) {
            setShowStepErrors(true);
            return;
        }
        setShowStepErrors(false);
        if (currentStep < STEPS.length - 1) setCurrentStep((s) => s + 1);
    };

    const handleBack = () => {
        setShowStepErrors(false);
        if (currentStep > 0) setCurrentStep((s) => s - 1);
    };

    const handleSave = async () => {
        if (!id || !form) return;
        if (!form.latitude || !form.longitude) {
            setSaveError('Latitude and longitude are required. Go to Location step to add coordinates.');
            return;
        }
        setSaving(true);
        setSaveError(null);

        try {
            const updates: Promise<unknown>[] = [];

            // General patch (same fields as create)
            const patchDto: Record<string, unknown> = {
                notes: form.notes || null,
                district: form.district || null,
                municipality: form.municipality || null,
                city_island: form.city_island || null,
                location_name: form.location_name || null,
                substrate_id: form.substrate_id || null,
                area: form.area ? parseFloat(form.area) : null,
                length: form.length ? parseFloat(form.length) : null,
                num_of_participant: form.num_of_participant
                    ? parseInt(form.num_of_participant)
                    : null,
                team_id: form.team_id || null,
                activityId: form.activityId || null,
            };
            if (form.duration_from_hour || form.duration_to_hour) {
                patchDto.duration = {
                    from: {
                        hour: parseInt(form.duration_from_hour) || 0,
                        minute: parseInt(form.duration_from_minute) || 0,
                    },
                    to: {
                        hour: parseInt(form.duration_to_hour) || 0,
                        minute: parseInt(form.duration_to_minute) || 0,
                    },
                };
            }
            updates.push(updateCleanUp(id, patchDto));

            // Location (lat/lng) if changed
            const origLat = String(originalCleanUp?.location?.latitude ?? '');
            const origLng = String(originalCleanUp?.location?.longitude ?? '');
            if (form.latitude !== origLat || form.longitude !== origLng) {
                if (form.latitude && form.longitude) {
                    updates.push(
                        updateLocation(id, {
                            location: {
                                latitude: parseFloat(form.latitude),
                                longitude: parseFloat(form.longitude),
                            },
                        })
                    );
                }
            }

            // Collected weight — always sync
            updates.push(
                updateCollectedWeight(id, {
                    no_litter_found: form.no_litter_found,
                    collected_weight: form.no_litter_found
                        ? []
                        : Object.entries(form.collected_weights)
                              .filter(([, v]) => v.wet || v.dry)
                              .map(([wasteTypeId, v]) => ({
                                  waste_type: wasteTypeId,
                                  dry: v.dry ? parseInt(v.dry) : undefined,
                                  wet: v.wet ? parseInt(v.wet) : undefined,
                              })),
                })
            );

            // Collected items — send all categories present in form
            Object.entries(form.collected_items).forEach(([wasteCategoryId, items]) => {
                const itemRecords = Object.entries(items)
                    .filter(([, amount]) => amount && parseInt(amount) > 0)
                    .map(([itemId, amount]) => ({
                        waste_category_item: itemId,
                        amount: parseInt(amount),
                    }));
                updates.push(
                    updateCollectedItems(id, {
                        waste_category: wasteCategoryId,
                        items: itemRecords,
                    })
                );
            });

            // Litter bags — always sync
            updates.push(
                updateLitterBags(id, {
                    resources: form.litter_bags.map((lb) => ({
                        resource_id: lb.resource_id,
                        full: lb.full ? parseInt(lb.full) : undefined,
                        half: lb.half ? parseInt(lb.half) : undefined,
                    })),
                })
            );

            // General resources — always sync
            updates.push(
                updateResources(id, {
                    resources: form.general_resources.map((gr) => ({
                        resource_id: gr.resource_id,
                        amount: parseInt(gr.amount) || 0,
                    })),
                })
            );

            // Site types — always sync
            updates.push(updateSiteTypes(id, {site_types: form.site_types}));

            // Additional location info — always sync
            updates.push(
                updateAdditionalInfo(id, {location_infos: form.additional_location_info})
            );

            // Weather conditions — always sync
            const weatherToSave = form.weather_conditions.filter((wc) => wc.condition_id);
            updates.push(
                updateWeatherConditions(id, {
                    conditions: weatherToSave.map((wc) => ({
                        condition_id: wc.condition_id,
                        option_id: wc.option_id || null,
                        value: wc.value ? parseFloat(wc.value) : undefined,
                    })),
                })
            );

            const results = await Promise.allSettled(updates);
            const failed = results.filter((r) => r.status === 'rejected');
            if (failed.length > 0) {
                setSaveError(`${failed.length} update(s) failed. Some data may not have been saved.`);
                setSaving(false);
                return;
            }

            // Refresh the Redux store with the latest data
            const refreshed = await fetchCleanUById(id);
            dispatch(replaceCleanUp(refreshed));

            const d = returnDate ?? originalCleanUp?.date.slice(0, 7);
            navigate(`/clean-ups${d ? `?date=${d}` : ''}`);
        } catch {
            setSaveError('Failed to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <PageWrapper>
                <PageHeader title="Edit Clean-up" />
                <PageContentWrapper>
                    <div className={s.container}>
                        <div className={s.loadingState}>Loading clean-up data...</div>
                    </div>
                </PageContentWrapper>
            </PageWrapper>
        );
    }

    if (!form || !originalCleanUp) {
        return (
            <PageWrapper>
                <PageHeader title="Edit Clean-up" />
                <PageContentWrapper>
                    <div className={s.container}>
                        <div className={s.loadingState}>Clean-up not found.</div>
                    </div>
                </PageContentWrapper>
            </PageWrapper>
        );
    }

    const programName =
        programs.find((p) => p.id === originalCleanUp.program)?.name ?? originalCleanUp.program;
    const collectorName = originalCleanUp.created_by
        ? `${originalCleanUp.created_by.first_name} ${originalCleanUp.created_by.last_name}`
        : '—';

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className={s.stepBody}>
                        <div className={s.subSectionTitle}>Program Details</div>
                        <div className={s.summaryGrid}>
                            <div className={s.summaryItem}>
                                <span className={s.summaryLabel}>Program</span>
                                <span className={s.summaryValue}>{programName}</span>
                            </div>
                            <div className={s.summaryItem}>
                                <span className={s.summaryLabel}>Date</span>
                                <span className={s.summaryValue}>
                                    {convertDateToString(new Date(originalCleanUp.date))}
                                </span>
                            </div>
                            <div className={s.summaryItem}>
                                <span className={s.summaryLabel}>Collector</span>
                                <span className={s.summaryValue}>{collectorName}</span>
                            </div>
                            <div className={s.summaryItem}>
                                <span className={s.summaryLabel}>Coordinates</span>
                                <span className={s.summaryValue}>
                                    {originalCleanUp.location?.latitude &&
                                    originalCleanUp.location?.longitude ? (
                                        <>
                                            <span className={s.coordNote}>
                                                DD · Lat {originalCleanUp.location.latitude}, Lng {originalCleanUp.location.longitude}
                                            </span>
                                            <span className={s.coordNote}>
                                                DMS · {toDMS(parseFloat(originalCleanUp.location.latitude), true)} (Lat), {toDMS(parseFloat(originalCleanUp.location.longitude), false)} (Lng)
                                            </span>
                                        </>
                                    ) : '—'}
                                </span>
                            </div>
                        </div>
                        <p className={s.fieldHint} style={{marginTop: 16}}>
                            Program, date, and collector cannot be changed after creation. All other
                            data can be edited in the following steps.
                        </p>
                    </div>
                );
            case 1:
                return (
                    <Step2Litter
                        form={form}
                        wasteTypes={programScopedData?.wasteTypes ?? []}
                        wasteCategories={programScopedData?.wasteCategories ?? []}
                        onChange={updateForm}
                    />
                );
            case 2:
                return (
                    <Step3Location
                        form={form}
                        substrateOptions={programScopedData?.substrateOptions ?? []}
                        siteTypes={programScopedData?.siteTypes ?? []}
                        additionalInfo={programScopedData?.additionalInfo ?? []}
                        onChange={updateForm}
                        showErrors={showStepErrors}
                    />
                );
            case 3:
                return (
                    <Step4Resources
                        form={form}
                        litterBags={programScopedData?.litterBags ?? []}
                        generalResources={programScopedData?.generalResources ?? []}
                        onChange={updateForm}
                    />
                );
            case 4:
                return (
                    <Step5Remarks
                        form={form}
                        activities={programScopedData?.activities ?? []}
                        teams={programScopedData?.teams ?? []}
                        weatherConditions={programScopedData?.weatherConditions ?? []}
                        programSelected={true}
                        onChange={updateForm}
                    />
                );
            case 6:
                return (
                    <Step6Review
                        form={form}
                        programs={programs}
                        activities={programScopedData?.activities ?? []}
                        submitError={saveError}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <PageWrapper>
            <PageHeader title="Edit Clean-up" />
            <PageContentWrapper>
                <div className={s.container}>
                    <div className={s.stepper}>
                        {STEPS.map((step, index) => (
                            <div
                                key={index}
                                className={[
                                    s.stepItem,
                                    index === currentStep ? s.active : '',
                                    index < currentStep ? s.completed : '',
                                    index !== currentStep && !saving ? s.clickable : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                onClick={index !== currentStep && !saving ? () => { setCurrentStep(index); setShowStepErrors(false); setSaveError(null); } : undefined}
                            >
                                <div className={s.stepIndicator}>
                                    {index < currentStep ? (
                                        '✓'
                                    ) : step.icon ? (
                                        step.icon
                                    ) : (
                                        index + 1
                                    )}
                                </div>
                                <div className={s.stepLabels}>
                                    <span className={s.stepTitle}>{step.label}</span>
                                    <span className={s.stepDesc}>{step.description}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className={s.stepCard}>
                        {currentStep !== 5 && renderStep()}
                        <div style={{display: currentStep === 5 ? '' : 'none'}}>
                            <StepGallery
                                cleanUpId={id!}
                                initialSpots={originalCleanUp?.images ?? []}
                            />
                        </div>
                    </div>

                    <div className={s.navigation}>
                        <button
                            type="button"
                            onClick={() => {
                                const d = returnDate ?? originalCleanUp?.date.slice(0, 7);
                                navigate(`/clean-ups${d ? `?date=${d}` : ''}`);
                            }}
                            className={s.cancelButton}
                        >
                            Cancel
                        </button>
                        <div className={s.navSpacer} />
                        {currentStep > 0 && (
                            <button
                                type="button"
                                onClick={handleBack}
                                className={s.backButton}
                            >
                                Back
                            </button>
                        )}
                        {currentStep < STEPS.length - 1 ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                className={s.nextButton}
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleSave}
                                className={s.submitButton}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        )}
                    </div>
                </div>
            </PageContentWrapper>
        </PageWrapper>
    );
};
